import { Link } from '@tanstack/react-router';
import { LockIcon } from 'lucide-react';
import { useContext, type ReactNode } from 'react';

import { EmptyState } from '@/shared/components/EmptyState';
import { PageHeader } from '@/shared/components/PageHeader';
import { PageSectionContext } from '@/shared/components/pageSection';

import { ACCESS_COPY } from '../accessCopy';
import { useCanWork } from '../canWork';
import { RequestAccess } from './RequestAccess';

/**
 * A members' page — Secrets, Connections — for someone who reached it by its
 * address while only reading the workspace (spec 189). The sidebar never offers it
 * to them (spec 188); the service refuses its reads (spec 151 FR-005), so rather
 * than a page that says "could not be loaded", it says why.
 *
 * In the page's own frame (spec 193): the title stays the page they asked for, and
 * the body offers the two ways on — ask to join, or go where they can look.
 */
export function MembersOnly({
  what,
  workspaceId,
  frame = true,
  children,
}: {
  /** What the page holds, as a plural noun: "Secrets", "Connections". */
  what: string;
  workspaceId: string;
  /**
   * `false` draws only the refusal, for a tab whose layout already drew the band
   * and the strip (Settings → People, spec 227): a second header would be a
   * second level-one heading on the page.
   */
  frame?: boolean;
  children: ReactNode;
}) {
  const canWork = useCanWork();
  // Under a banded section (spec 227) the band's standing pill already offers
  // Request access; a second button under it would ask twice on one page.
  const banded = useContext(PageSectionContext)?.surface === 'inverse';
  if (canWork) return children;
  const body = (
    <EmptyState
      icon={LockIcon}
      title={ACCESS_COPY.membersPageTitle}
      description={ACCESS_COPY.membersPageBody(what)}
      action={
        <div className="flex flex-col items-center gap-3">
          {banded ? null : <RequestAccess workspaceId={workspaceId} />}
          <Link
            to="/workspaces/$workspaceId"
            params={{ workspaceId }}
            // Foreground and underlined, not Light Blue: small text on the
            // dark theme's surfaces fails as Light Blue (WD-04).
            className="text-foreground focus-visible:ring-ring rounded-sm text-sm underline underline-offset-2 hover:text-foreground/80 focus-visible:ring-3 focus-visible:outline-none"
          >
            {ACCESS_COPY.toOverview}
          </Link>
        </div>
      }
    />
  );
  if (!frame) return body;
  return (
    // `PagePlaceholder`'s layout, without its description under the title: the
    // body below says it, and saying it twice would be the page's whole content.
    <section className="flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6 pb-12">
      <PageHeader eyebrow="Workspace" title={what} />
      {body}
    </section>
  );
}
